import { defineStore } from 'pinia';
import type { TenantSettingType } from '@/types/TenantSettingType';
import type {
    TenantSettingsState,
    TenantSettingsGetters,
    TenantSettingsActions,
} from '@/types/stores/TenantSettingsStoreTypes';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';

export const useTenantSettingsStore = defineStore<
    'tenantSettings',
    TenantSettingsState,
    TenantSettingsGetters,
    TenantSettingsActions
>('tenantSettings', {
    state: () => ({
        items: [],
        totalItems: 0,
        isLoading: false,
        currentTenantId: null,
    }),
    getters: {
        getEndpoint:
            () =>
                (tenantId: number | null): string =>
                    tenantId ? `/tenants/${tenantId}/settings` : '/tenant/settings',
        getVisibleSettings: (state: TenantSettingsState): TenantSettingType[] =>
            state.items.filter(setting => !setting.hidden),
        getSettingByKey:
            (state: TenantSettingsState) =>
                (key: string): TenantSettingType | undefined =>
                    state.items.find(item => item.key === key),
    },
    actions: {
        createEmptyTenantSetting(): TenantSettingType {
            return {
                id: -1,
                key: '',
                value: '',
                description: '',
                hidden: false,
            };
        },

        async fetchTenantSettings(tenantId: number | null = null): Promise<void> {
            this.isLoading = true;
            this.currentTenantId = tenantId;

            const endpoint = this.getEndpoint(tenantId);

            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint,
                onSuccess: response => {
                    this.items = response.data.items;
                    this.totalItems = response.data.total;
                },
            });
            this.isLoading = false;
        },

        async createTenantSetting(setting: TenantSettingType, tenantId: number | null = null): Promise<boolean> {
            this.isLoading = true;
            const endpoint = this.getEndpoint(tenantId);
            let success = false;

            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint,
                data: {
                    key: setting.key,
                    value: setting.value,
                    description: setting.description,
                    hidden: setting.hidden,
                },
                canShowSnackbar: false,
                onSuccess: response => {
                    const newSetting = response.data.data;
                    const index = this.items.findIndex(item => item.id === setting.id);
                    if (index >= 0) {
                        this.items[index] = newSetting;
                    } else {
                        this.items.push(newSetting);
                    }
                    success = true;
                },
            });
            this.isLoading = false;

            return success;
        },

        async updateTenantSetting(setting: TenantSettingType, tenantId: number | null = null): Promise<boolean> {
            this.isLoading = true;
            const endpoint = this.getEndpoint(tenantId);
            let success = false;

            await handleRequest({
                method: RequestMethodEnum.Put,
                endpoint: `${endpoint}/${setting.key}`,
                data: {
                    value: setting.value,
                    description: setting.description,
                    hidden: setting.hidden,
                },
                canShowSnackbar: false,
                onSuccess: response => {
                    const updatedSetting = response.data.data;
                    const index = this.items.findIndex(item => item.key === setting.key);
                    if (index >= 0) {
                        this.items[index] = updatedSetting;
                    }
                    success = true;
                },
            });
            this.isLoading = false;

            return success;
        },

        async deleteTenantSetting(key: string, tenantId: number | null = null): Promise<boolean> {
            this.isLoading = true;
            const endpoint = this.getEndpoint(tenantId);
            let success = false;

            await handleRequest({
                method: RequestMethodEnum.Delete,
                endpoint: `${endpoint}/${key}`,
                successMessageKey: 'messages.tenantSettingDeleted',
                onSuccess: () => {
                    const index = this.items.findIndex(item => item.key === key);
                    if (index >= 0) {
                        this.items.splice(index, 1);
                    }
                    success = true;
                },
            });
            this.isLoading = false;

            return success;
        },

        async saveTenantSetting(setting: TenantSettingType, tenantId: number | null = null): Promise<boolean> {
            if (setting.id && setting.id > 0) {
                return this.updateTenantSetting(setting, tenantId);
            } else {
                return this.createTenantSetting(setting, tenantId);
            }
        },
    },
});
